import React, { useEffect, useContext } from 'react'
import { Button, Grid, GridItem } from '@deal/components'
import ContentfulQuery from '#src/containers/Contentful'
import { AnalyticsContext } from '#src/containers/Analytics'
import { Link } from 'react-router-dom'
import { Experiment } from '@deal/experiment-js'
import {
  SellableBrandCategoryComponent,
  PathSlugForCategoryComponent
} from '#src/generated/graphql'
import { generateCreateUrlFromCategory } from '#src/services/path'
import styles from './styles.css'

interface EmbeddedLandingPageProps {
  sellableId?: string
}

interface EmbeddedLandingPageResponse {
  loading: boolean
  content: {
    header: string
    bullets: {
      header: string
      body: string
    }[]
  }
}

interface EmbeddedLandingPageContentProps {
  pathCategorySlug?: string | null
  contentfulVariation: string
  header: React.ReactNode
  bullets: {
    header: React.ReactNode
    body: React.ReactNode
  }[]
}

const defaultHeader = 'Shop with an expert. Love what you buy.'

const defaultBullets = [
  {
    header: "Tell us what you're looking for",
    body: 'Start with a 2 minute quiz'
  },
  {
    header: 'Talk to an expert',
    body: 'Ask them everything you want to know.'
  },
  {
    header: 'Get a personalized list of recommendations',
    body: "Your expert won't quit til you're happy"
  }
]

const EmbeddedLandingPageContent: React.SFC<EmbeddedLandingPageContentProps> = ({
  header = defaultHeader,
  bullets = defaultBullets,
  contentfulVariation,
  pathCategorySlug
}) => {
  const analytics = useContext(AnalyticsContext)

  useEffect(() => {
    analytics?.events.engagementChannelViewed({
      engagement_channel: 'embedded-landing-page',
      variation: contentfulVariation,
      category: pathCategorySlug || ''
    })
  }, [])

  return (
    <div className={styles.wrapper}>
      <Grid>
        <GridItem col={2}>
          <div className={styles.header}>{header}</div>
          <div className={styles.bullets}>
            {bullets.slice(0, 3).map((bullet, idx) => (
              <div className={styles.bullet} key={idx}>
                <div className={styles.number}>{idx + 1}</div>
                <div className={styles.copy}>
                  <div className={styles.bulletHeader}>{bullet.header}</div>
                  <div className={styles.bulletCopy}>{bullet.body}</div>
                </div>
              </div>
            ))}
          </div>
          <div className={styles.actions}>
            <Link
              to={generateCreateUrlFromCategory({
                maybeCategorySlug: pathCategorySlug,
                sourceKey: 'embedded-landing-page-engagement-channel',
                pathIntent: 'request'
              })}
            >
              <Button
                className={styles.submit}
                onClick={() => {
                  analytics?.events.engagementChannelSubmitted({
                    engagement_channel: 'embedded-landing-page',
                    variation: contentfulVariation,
                    category: pathCategorySlug || ''
                  })
                }}
              >
                Get free expert advice
              </Button>
            </Link>
          </div>
        </GridItem>
      </Grid>
    </div>
  )
}

const EmbeddedLandingPage: React.SFC<EmbeddedLandingPageProps> = ({ sellableId }) => {
  if (!sellableId) {
    return null
  }

  return (
    <SellableBrandCategoryComponent
      variables={{
        friendlyIdOrId: sellableId
      }}
    >
      {({ data }) => {
        if (data && data.findSellable && data.findSellable.categories.length) {
          const sellableCategory = data.findSellable.categories[0]

          return (
            <PathSlugForCategoryComponent
              variables={{
                slug: sellableCategory.slug
              }}
            >
              {({ data: pathData }) => {
                if (
                  pathData &&
                  pathData.categoryBySlug &&
                  pathData.categoryBySlug.ancestorWithPath
                ) {
                  const pathCategorySlug = pathData.categoryBySlug.ancestorWithPath.slug

                  return (
                    <Experiment id={`embedded-landing-page-${pathCategorySlug}`} default="v1">
                      {({ loading, result: experimentResult }) => {
                        if (loading) {
                          return null
                        }

                        const contentfulVariation = `${pathCategorySlug}-${experimentResult}`

                        return (
                          <ContentfulQuery
                            template="embeddedLandingPage"
                            variation={contentfulVariation}
                          >
                            {({ content }: EmbeddedLandingPageResponse) => {
                              if (content) {
                                return (
                                  <EmbeddedLandingPageContent
                                    header={content.header}
                                    contentfulVariation={contentfulVariation}
                                    pathCategorySlug={pathCategorySlug}
                                    bullets={content.bullets}
                                  />
                                )
                              }

                              return null
                            }}
                          </ContentfulQuery>
                        )
                      }}
                    </Experiment>
                  )
                }

                return null
              }}
            </PathSlugForCategoryComponent>
          )
        }

        return null
      }}
    </SellableBrandCategoryComponent>
  )
}

export default EmbeddedLandingPage
